import * as React from 'react';
import cx from 'classnames';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { State } from '../redux/state';
import { withLocalization, LProps } from '../utils/wrappers';
import { CompetitionEntityScore, Entity, CompetitionEntity } from '../userApi';
import { Link } from 'react-router-dom';
import { makeEntityPath } from '../utils/routerHelper';
import { isHostEntity, isHiddenFromScoreboard } from '../utils/competitionLabels';
import { estimateEntityDollarSavings } from '../utils/actionSavings';

interface ManualCampusScoreProps {
  score: CompetitionEntityScore;
  rank: number;
  showDollarSavings: boolean;
  showWater: boolean;
}

interface CampusScoreProps extends ManualCampusScoreProps {
  competitionEntity: CompetitionEntity;
  university: Entity;
  dollarsSaved: number;
}

const CampusScore = ({
  rank, score, university, competitionEntity, showDollarSavings, showWater, dollarsSaved, t, formatNumber
}: LProps<CampusScoreProps>) => {
  if (!university || !competitionEntity) {
    return null;
  }
  // Legacy savingsWaterOz stores gallons (see Action Savings methodology).
  const waterGallons = Number(score.savingsWaterOz || 0);
  return (
  <tr>
    <td>#{rank}</td>
    <td>
      <Link to={makeEntityPath(competitionEntity.competitionID, university, t)}>
        {university.name || university.abbreviation}
      </Link>
    </td>
    <td className="cc__scoreboard__percentage">{competitionEntity.memberCount}</td>
    <td className="cc__scoreboard__percentage">{formatNumber(competitionEntity.pledgeCount || 0, 0)}</td>
    <td className="cc__scoreboard__points">{formatNumber(score.points, 0)}</td>
    <td className="cc__scoreboard__points">{formatNumber(score.savingsCO2Lbs || 0, 0)}</td>
    {showWater && (
      <td className="cc__scoreboard__points">{formatNumber(waterGallons, 0)}</td>
    )}
    {showDollarSavings && (
      <td className="cc__scoreboard__points">${formatNumber(Math.round(dollarsSaved), 0)}</td>
    )}
  </tr>
  );
};

const ConnectedCampusScore = compose(
  connect((state: State, ownProps: ManualCampusScoreProps) => ({
    competitionEntity: state.competitionEntities[ownProps.score.entityID],
    university: state.entities[ownProps.score.entityID],
    dollarsSaved: estimateEntityDollarSavings(ownProps.score, state.competitionPledges)
  })),
  withLocalization('scoreboard')
)(CampusScore) as React.ComponentClass<ManualCampusScoreProps>;

interface ManualProps {
  /** Landing widget: denser type, omit water to keep the table readable. */
  compact?: boolean;
}

interface Props extends ManualProps {
  campusScores: CompetitionEntityScore[];
  showDollarSavings: boolean;
}

const ScoreboardCampuses = ({
  t, campusScores, showDollarSavings, compact
}: LProps<Props>) => {
  const showWater = Boolean(showDollarSavings) && !compact;
  return (
  <div className="cc__scoreboard__table-wrap">
  <table className={cx({ 'cc__scoreboard__table--compact': compact })}>
    <thead>
      <tr>
        <th />
        <th />
        <th className="cc__scoreboard__percentage">{compact ? t('Part.') : t('Participants')}</th>
        <th className="cc__scoreboard__percentage">{t('Actions')}</th>
        <th className="cc__scoreboard__points">{t('Points')}</th>
        <th
          className="cc__scoreboard__points"
          title={t('Pounds CO2e saved')}
          dangerouslySetInnerHTML={{ __html: compact ? t('CO<sub>2</sub>e') : t('Lbs CO<sub>2</sub>e') }}
        />
        {showWater && (
          <th className="cc__scoreboard__points" title={t('Water saved (gallons)')}>{t('Water (gal)')}</th>
        )}
        {showDollarSavings && (
          <th className="cc__scoreboard__points" title={t('Estimated San Francisco bill savings')}>{t('$ Saved')}</th>
        )}
      </tr>
    </thead>
    <tbody>
      {campusScores.map((entityScore, i) => (
        <ConnectedCampusScore
          key={entityScore.id}
          score={entityScore}
          rank={i + 1}
          showDollarSavings={showDollarSavings}
          showWater={showWater}
        />
      ))}
    </tbody>
  </table>
  </div>
  );
};

const mapStateToProps = (state: State, ownProps: ManualProps): Props => {
  const scores = (state.competitionEntityScores && state.competitionEntityScores.university) || [];
  const competition = state.competition;
  const competitionName = String((competition && competition.name) || '').toLowerCase();
  const showDollarSavings = competitionName === 'ccsf' || competitionName === 'sf' || competitionName === 'sfenvironment';
  return {
    compact: ownProps.compact,
    showDollarSavings,
    campusScores: scores.filter((score) => {
      const entity = state.entities && state.entities[score.entityID];
      return entity && !isHostEntity(entity, competition) && !isHiddenFromScoreboard(entity, competition);
    })
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('scoreboard')
)(ScoreboardCampuses) as React.ComponentClass<ManualProps>;
